// TEMPLATES

import { Box, Flex } from '@chakra-ui/react'
import {
  A4_DIMS_PIXELS,
  A4_MARGINS_PIXELS,
  AssessmentDocument,
  defaultInitialValue,
  ErrorWithMessage,
  isPracticeContract,
  isSignableDocument,
  SignableDocument,
  WithId,
} from '@hb/shared'
import React, {
  forwardRef,
  KeyboardEvent,
  PropsWithChildren,
  useCallback,
  useContext,
  useEffect,
  useImperativeHandle,
  useMemo,
  useRef,
  useState,
} from 'react'
import { TransformComponent, TransformWrapper } from 'react-zoom-pan-pinch'
import { createEditor } from 'slate'
import { withHistory } from 'slate-history'
import {
  Editable,
  ReactEditor,
  RenderElementProps,
  RenderLeafProps,
  Slate,
  withReact,
} from 'slate-react'
import { pipe } from '../utils'
import { TextEditorContext, TextEditorContextValue } from './context'
import { DefaultRenderLeaf } from './DefaultRenderLeaf'
import { EditorToolbar } from './EditorToolbar'
import { downloadFromEditor } from './TemplateToolbar/utils'
import { ReadOnlyEditorProps, TextEditorProps } from './types'

import { PopUpMessageContext, ScreenContext } from '@hb/react'
import { downloadFromStorage } from '../../backend/storage/utils'
import { downloadConsentForm } from '../../hooks/consentForms/useConsentFormDownloadUpload'
import { downloadPracticeContract } from '../../hooks/practiceContracts/usePracticeContractDownload'
import { CustomEditor, CustomRenderElementProps } from '../../types/editor'
import { GoToNextFieldRichText } from '../PdfView/GoToNextField'
import { DocumentDownloadButton } from '../Templates/DocumentDownloadButton'
import marginGuidelinesSvg from './assets/marginGuidelines.svg'
import marginGuidelinesTiledSvg from './assets/marginGuidelinesTiled.svg'
import { TextEditorDocumentFormProvider } from './DocumentFormProvider'
import { ImageElement } from './Elements/ImageElement'
import { LinkElement } from './Elements/LinkElement'
import { DefaultElement } from './Elements/SpanTemplateElement'
import { EditorFieldElementView } from './Elements/TemplateFieldElementView'
import { VariableElement } from './Elements/VariableElement'
import './styles.css'
import { TemplateFieldSelectionContext } from './TemplateFieldSelectionContext'

export const DefaultRenderElement = (props: CustomRenderElementProps) => {
  switch (props.element.type) {
    case 'variable':
      return <VariableElement {...props} />
    case 'field':
      return <EditorFieldElementView {...props} />
    case 'image':
      return <ImageElement node={props.element} {...props} />
    case 'link':
      return <LinkElement {...props} />
    default:
      return <DefaultElement {...props} />
  }
}

interface BaseEditorBodyProps {
  mode: 'View' | 'Edit'
  readOnly: boolean | undefined
  handleRenderLeaf: (props: RenderLeafProps) => React.ReactElement
  handleRenderElement: (props: RenderElementProps) => React.ReactElement
  handleKeyDown: (event: KeyboardEvent) => void
  editorPadding: { x: number; y: number }
}

interface MobileEditorBodyProps extends BaseEditorBodyProps {
  baseZoom: number | undefined
}

interface DesktopEditorBodyProps extends BaseEditorBodyProps {
  baseZoom: number | undefined
  width: number | undefined
}

const MobileEditorBody: React.FC<MobileEditorBodyProps> = ({
  mode,
  readOnly,
  handleRenderLeaf,
  handleRenderElement,
  handleKeyDown,
  editorPadding,
  baseZoom,
}) => {
  const containerRef = useRef<HTMLDivElement>(null)

  return (
    <Box
      ref={containerRef}
      position="relative"
      overflow="hidden"
      w="100%"
      h="100%"
      style={{
        touchAction: mode === 'View' || readOnly ? 'none' : 'auto',
      }}>
      <TransformWrapper
        key={`transform-${baseZoom || 1}`}
        initialScale={baseZoom || 1}
        minScale={0.5}
        maxScale={3}
        limitToBounds={true}
        centerOnInit={false}
        centerZoomedOut={false}
        panning={{
          disabled: mode === 'Edit',
          excluded: ['input', 'button'],
        }}
        wheel={{
          disabled: true,
        }}
        doubleClick={{
          disabled: true,
        }}>
        <TransformComponent
          wrapperStyle={{
            width: '100%',
            height: '100%',
          }}>
          <Editable
            readOnly={readOnly || mode === 'View'}
            spellCheck={mode === 'Edit'}
            renderLeaf={handleRenderLeaf}
            renderElement={handleRenderElement}
            onKeyDown={handleKeyDown}
            style={{
              width: A4_DIMS_PIXELS[0],
              minHeight: A4_DIMS_PIXELS[1],
              height: 'auto',
              padding: `${editorPadding.y}px ${editorPadding.x}px`,
              boxSizing: 'border-box',
              background: 'white',
              border: 'none',
              backgroundRepeat: 'repeat-y',
              outline: 'none',
            }}
          />
        </TransformComponent>
      </TransformWrapper>
    </Box>
  )
}

const DesktopEditorBody: React.FC<DesktopEditorBodyProps> = ({
  mode,
  readOnly,
  handleRenderLeaf,
  handleRenderElement,
  handleKeyDown,
  editorPadding,
  baseZoom,
  width,
}) => (
  <Flex
    position="relative"
    alignItems="flex-start"
    overflowY="auto"
    bg="gray.100"
    overflowX="hidden"
    w="100%"
    h="100%">
    <Editable
      readOnly={readOnly || mode === 'View'}
      spellCheck={mode === 'Edit'}
      renderLeaf={handleRenderLeaf}
      renderElement={handleRenderElement}
      onKeyDown={handleKeyDown}
      style={{
        width: A4_DIMS_PIXELS[0],
        minHeight: A4_DIMS_PIXELS[1],
        height: 'auto',
        backgroundColor: 'white',
        margin: '0 auto',
        padding: `${editorPadding.y}px ${editorPadding.x}px`,
        boxShadow: '0 0 10px rgba(0,0,0,0.1)',
        boxSizing: 'border-box',
        transformOrigin: '0 0',
        border: 'none',
        transform: `scale(${baseZoom || 1})`,
        transformBox: 'view-box',
        backgroundRepeat: 'repeat-y',
        outline: 'none',
      }}
    />
  </Flex>
)

export const ReadOnlyEditor: React.FC<PropsWithChildren<ReadOnlyEditorProps>> = props => (
  <TextEditor readOnly {...props} />
)

const useDownload = (
  editor: CustomEditor,
  withDownload: boolean | undefined,
  document: AssessmentDocument | WithId<SignableDocument> | undefined,
) => {
  const { processResponse } = useContext(PopUpMessageContext)

  const [downloadLoading, setDownloadLoading] = useState(false)

  const onDownload = useCallback(async () => {
    if (!withDownload) return

    setDownloadLoading(true)
    try {
      if (document && isSignableDocument(document)) {
        if (document.signedStoragePath) {
          const res = await downloadFromStorage(document.signedStoragePath)
          processResponse(res)
          return
        } else if (isPracticeContract(document)) {
          await downloadPracticeContract(document.id, document.practiceId)
          processResponse({ success: 'Practice contract downloaded successfully' })
          return
        } else {
          await downloadConsentForm(document.id, document.patientId)
          processResponse({ success: 'Consent form downloaded successfully' })
          return
        }
      } else {
        const res = await downloadFromEditor(editor)
        processResponse(res)
        return
      }
    } catch (e) {
      processResponse({ error: (e as ErrorWithMessage).message })
    } finally {
      setDownloadLoading(false)
    }
  }, [editor, processResponse, withDownload, document])
  return { downloadLoading, onDownload }
}

export const TextEditor = forwardRef<ReactEditor, PropsWithChildren<TextEditorProps>>(
  (
    {
      value,
      decorators,
      onChange,
      withDownload,
      document,
      onFormSubmit,
      toolbars,
      version,
      children,
      templateType,
      baseZoom,
      readOnly,
      height,
      style,
      width,
      modalWidth,
      smallMargins,
    }: TextEditorProps,
    ref,
  ) => {
    const [editor] = useState<CustomEditor>(
      decorators
        ? pipe(...decorators)(withReact(withHistory(createEditor())))
        : withReact(withHistory(createEditor())),
    )

    const [mode, setMode] = useState<'View' | 'Edit'>(readOnly ? 'View' : 'Edit')
    const [selectedFieldId, setSelectedFieldId] = useState<string | null>(null)
    const fieldSelection = useMemo(
      () => ({ selectedFieldId, setSelectedFieldId }),
      [selectedFieldId],
    )
    const { isMobile } = useContext(ScreenContext)

    useImperativeHandle(ref, () => editor)

    const handleRenderElement = useCallback(
      (props: RenderElementProps) =>
        DefaultRenderElement({
          ...props,
          version,
          mode,
          readOnly: !!readOnly,
          editor,
        }),
      [version, mode, readOnly, editor],
    )

    const handleRenderLeaf = useCallback(
      (props: RenderLeafProps) =>
        DefaultRenderLeaf({
          ...props,
          mode,
          editor,
          version,
        }),
      [mode, editor, version],
    )

    const handleKeyDown = useCallback(
      (event: KeyboardEvent) => {
        if (event.key === 'Tab') {
          event.preventDefault()
          editor.insertText('\u2003')
        }
      },
      [editor],
    )

    const editorDomElement = useRef<HTMLDivElement | null>(null)

    useEffect(() => {
      const domElement = ReactEditor.toDOMNode(editor, editor) as HTMLDivElement

      const onResize = () => {
        const elementHeight = domElement.scrollHeight

        if (mode === 'Edit') {
          domElement.style.setProperty(
            'background-image',
            elementHeight > A4_DIMS_PIXELS[1]
              ? `url(${marginGuidelinesTiledSvg})`
              : `url(${marginGuidelinesSvg})`,
          )
        } else {
          domElement.style.setProperty('background-image', 'none')
        }
      }
      onResize()
      new ResizeObserver(onResize).observe(domElement)
      editorDomElement.current = domElement
    }, [editor, baseZoom, mode])

    const contextData = useMemo<TextEditorContextValue>(
      () => ({
        mode,
        setMode,
        document: document ?? null,
        editorDomElement,
        modalWidth: modalWidth ?? width,
        width,
        height,
        baseZoom,
      }),
      [document, mode, width, height, baseZoom, modalWidth],
    )
    const editorPadding = useMemo(() => {
      if (smallMargins) {
        return { x: A4_MARGINS_PIXELS[1] / 2, y: A4_MARGINS_PIXELS[0] / 2 }
      }
      return { x: A4_MARGINS_PIXELS[1], y: A4_MARGINS_PIXELS[0] }
    }, [smallMargins])

    const { downloadLoading, onDownload } = useDownload(editor, withDownload, document)

    return (
      <Slate
        initialValue={value?.length ? value : defaultInitialValue}
        onChange={onChange ?? (() => {})}
        editor={editor}>
        <TextEditorContext.Provider value={contextData}>
          <TextEditorDocumentFormProvider
            readOnly={readOnly}
            onFormSubmit={onFormSubmit}
            width={width}
            modalWidth={modalWidth}
            document={document}
            height={height}
            style={style}>
            <TemplateFieldSelectionContext.Provider value={fieldSelection}>
              <Flex flexFlow="column" position="relative" w="100%" h="100%">
                <Box
                  // ref={headerRef}
                  height="auto"
                  background="whitesmoke"
                  position="relative"
                  zIndex={2}
                  top={0}
                  contentEditable={false}>
                  {withDownload ? (
                    <DocumentDownloadButton
                      onDownload={onDownload}
                      downloadLoading={downloadLoading}
                      document={document}
                    />
                  ) : null}
                  {children}
                  {!readOnly ? (
                    <>
                      <EditorToolbar />
                      {toolbars
                        ? toolbars.map((T, index) => (
                            <T
                              document={document}
                              templateType={templateType}
                              version={version}
                              key={index}
                              mode={mode}
                            />
                          ))
                        : null}
                    </>
                  ) : null}
                </Box>
                <Box
                  w="100%"
                  overflow="hidden"
                  flex={1}
                  position="relative"
                  minH="0"
                  onMouseDown={e => {
                    // Clicking anywhere in the editor body that isn't a field element clears the
                    // field selection. Field clicks re-set it on their own onClick.
                    if (!(e.target as HTMLElement).closest('[data-field-node]')) {
                      setSelectedFieldId(null)
                    }
                  }}>
                  {isMobile && readOnly ? (
                    <MobileEditorBody
                      mode={mode}
                      readOnly={readOnly}
                      handleRenderLeaf={handleRenderLeaf}
                      handleRenderElement={handleRenderElement}
                      handleKeyDown={handleKeyDown}
                      editorPadding={editorPadding}
                      baseZoom={baseZoom}
                    />
                  ) : (
                    <DesktopEditorBody
                      mode={mode}
                      readOnly={readOnly}
                      handleRenderLeaf={handleRenderLeaf}
                      handleRenderElement={handleRenderElement}
                      handleKeyDown={handleKeyDown}
                      editorPadding={editorPadding}
                      baseZoom={baseZoom}
                      width={width}
                    />
                  )}
                  <GoToNextFieldRichText />
                </Box>
              </Flex>
            </TemplateFieldSelectionContext.Provider>
            {/* {
                // guidelines for edit mode
                mode === 'Edit' ? (
                  <EditorMarginGuidelines/>
                ) : null
              } */}
          </TextEditorDocumentFormProvider>
        </TextEditorContext.Provider>
      </Slate>
    )
  },
)
